<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';

withDefaults(
  defineProps<{
    /** Formatted time to display, e.g. "23 min" or "14:35" */
    time?: string;
    /**
     * Formatted time shown smaller underneath: the next departure, or with `delay`
     * the timetable time the estimate supersedes, struck through.
     */
    secondaryTime?: string;
    /** Formatted delay, e.g. "+5 min": paints the time as a warning and adds it underneath */
    delay?: string;
    type?: 'realtime' | 'theoretical' | 'finished';
  }>(),
  {
    time: undefined,
    secondaryTime: undefined,
    delay: undefined,
    type: 'theoretical',
  },
);
</script>

<template>
  <span class="estimated-time" :class="{ 'estimated-time--delayed': delay }">
    <!-- The icon rides with the time: a wider details row underneath must not push them apart -->
    <span class="estimated-time__main">
      <BaseIcon
        v-if="type === 'realtime'"
        name="rss"
        :stroke-width="3"
        class="estimated-time__icon estimated-time__icon--realtime"
      />
      <BaseIcon
        v-if="type === 'finished'"
        name="moon"
        :stroke-width="3"
        class="estimated-time__icon estimated-time__icon--finished"
      />
      <span class="estimated-time__text">{{ type === 'finished' ? $t('time.finished') : time }}</span>
    </span>
    <span v-if="secondaryTime || delay" class="estimated-time__details">
      <span v-if="delay" class="estimated-time__delay">{{ delay }}</span>
      <span v-if="secondaryTime" class="estimated-time__secondary">{{ secondaryTime }}</span>
    </span>
  </span>
</template>

<style lang="scss">
.estimated-time {
  align-items: flex-end;
  display: inline-flex;
  flex-direction: column;
  gap: 6px;

  &__main {
    align-items: center;
    display: flex;
    gap: $spacing-1;
  }

  &__icon {
    flex-shrink: 0;

    &--realtime {
      color: $color-primary;
      font-size: 16px;
      transform: rotate(270deg);
    }

    &--finished {
      color: $color-secondary;
      font-size: 16px;
    }
  }

  &__text {
    color: $color-neutral-highest;
    font: $text-bold-medium;
    letter-spacing: $text-bold-medium-spacing;
    white-space: nowrap;
  }

  &__details {
    display: flex;
    gap: $spacing-2;
  }

  &__delay {
    color: $color-warning;
    font: $label-smaller;
    letter-spacing: $label-smaller-spacing;
    white-space: nowrap;
  }

  &__secondary {
    color: $color-neutral-higher;
    font: $label-smaller;
    letter-spacing: $label-smaller-spacing;
    white-space: nowrap;
  }

  // A late train: the estimate warns, the timetable it replaces is struck through
  &--delayed {
    .estimated-time__text {
      color: $color-warning;
    }

    .estimated-time__secondary {
      text-decoration: line-through;
    }
  }
}
</style>
